import View from '../../View';

/**
 * RectificationForm
 */
class RectificationForm extends View {

  /**
   * RectificationForm constructor
   * @param node
   */
  constructor(node) {
    super(node);

    //Initialize after a bit, otherwise TinyMCE would crash
    window.addEventListener('load', this.initialize);
  }

  /**
   * Initialize only after the document has been loaded
   */
  initialize = () => {
    //"Properties"
    this.busy = false;
    this.currentNode = null;
    this.editor = false;
    this.shown = false;
    this.dialogueShown = false;
    this.loading = false;
    this.selection = null;

    //Dom elements
    this.articleContainer = document.querySelector('.js-article-container');
    this.overlay = document.querySelector('.rectification-overlay');
    this.wysiwygContainers = Array.from(this.articleContainer.querySelectorAll('.js-wysiwyg-container'));
    this.closeButton = this.node.querySelector('.js-rectification-form-close');
    this.messageContainer = this.node.querySelector('.js-rectification-form-message');
    this.dialogue = document.querySelector('.js-rectificationDialogue');
    this.rectifyButton = document.querySelector('.js-rectificationDialogue-rectify');
    this.copyButton = document.querySelector('.js-rectificationDialogue-copy');

    //Form inputs
    this.textInput = this.node.querySelector('.js-rectification-new-text');
    this.email = this.node.querySelector('.js-rectification-email');
    this.name = this.node.querySelector('.js-rectification-name');
    this.thePot = this.node.querySelector('.js-rectification-the-pot');
    this.newContent = '';
    const fakeSubmit = this.node.querySelector('.js-article-comments-form-submit');

    //Error containers
    this.newContentError = this.node.querySelector('.js-rectification-newContent-error');
    this.emailError = this.node.querySelector('.js-rectification-email-error');

    //Events
    this.closeButton.addEventListener('click', this.closeButtonClickHandler);
    this.node.addEventListener('submit', this.submitHandler);
    fakeSubmit.addEventListener('click', this.submitHandler);
    this.email.addEventListener('focus', this.fieldFocusHandler);
    this.name.addEventListener('focus', this.fieldFocusHandler);
    document.addEventListener('click', this.clickOutsideHandler);
    this.rectifyButton.addEventListener('click', this.startRectifying);
    this.copyButton.addEventListener('click', this.copyText);
    this.wysiwygContainers.forEach(
      (wysiwygContainer) => {
        wysiwygContainer.addEventListener('mouseup', this.mouseUpHandler);
      }
    );

    //Initialize tinyMCE
    this.initTinyMCE();
  };

  /**
   * Initialize the TinyMCE wysiwyg editor
   */
  initTinyMCE = () => {
    this.editor = tinymce.init({
      selector: '.js-rectification-new-text',
      menubar: false,
      theme: 'modern',
      plugins: "autoresize",
      toolbar: 'bold italic strikethrough subscript superscript | removeformat',
      autoresize_bottom_margin: 10,
      autoresize_max_height: 400,
      formats: {
        strikethrough: {inline: 's'},
      },
      init_instance_callback: function (editor) {
        editor.on('focus', this.fieldFocusHandler);
      }.bind(this)
    });
  };

  /**
   * Handle mouse up events (could mean there is a selection)
   * @param e
   */
  mouseUpHandler = (e) => {
    if (this.busy) {
      return;
    }

    //Get the selection temporarily
    this.currentNode = this.getSelectedNode();

    //If there's a proper selection
    if (this.currentNode !== null) {
      this.busy = true;

      //Select the full node
      this.setSelectedNode();

      //Wait with rectifying until the user has selected this
      this.showDialogue();
    }
  };

  /**
   * Show a dialogue to let the user choose between rectifying or selecting text
   */
  showDialogue = () => {
    //Delay, otherwise the click outside handler will fire immediately
    window.setTimeout(
      function () {
        this.dialogueShown = true;
      }.bind(this),
      10
    );

    this.dialogue.classList.remove('mod-hidden');
  };

  /**
   * Hide the dialogue
   */
  hideDialogue = () => {
    this.dialogueShown = false;

    this.dialogue.classList.add('mod-hidden');
  };

  /**
   * Copy the selected text and clear the selection
   * @param e
   */
  copyText = (e) => {
    e.preventDefault();

    //Dialogue is no longer necessary
    this.hideDialogue();

    //Set the selection for a brief moment (has been lost because of the clicking)
    this.setSelectedNode();

    //Copy the selection the user has made
    document.execCommand("Copy");

    //Wipe the selection
    this.clearSelectedText();

    //No longer busy
    this.busy = false;
  };

  /**
   * Start making a rectification
   * @param e
   */
  startRectifying = (e) => {
    e.preventDefault();

    this.hideDialogue();

    if (this.currentNode !== null) {
      this.showForm();
    }
  };

  /**
   * Get the selected text
   * @returns {*}
   */
  getSelectedNode = () => {
    if (window.getSelection) {
      const selection = window.getSelection();
      this.selection = selection;

      if (selection.toString().length > 0) {
        let parentNode = selection.getRangeAt(0).startContainer.parentNode,
          correctElement = false;

        while (!correctElement) {
          if (parentNode.nodeName !== "I" && parentNode.nodeName !== "STRONG" && parentNode.nodeName !== "B" &&
            parentNode.nodeName !== "A" && parentNode.nodeName !== "SUP" && parentNode.nodeName !== "SUB" &&
            parentNode.nodeName !== "BOLD" && parentNode.nodeName !== "ITALIC" &&
            parentNode.nodeName !== "Q" && parentNode.nodeName !== "S"
          ) {
            correctElement = true;
          }
          else {
            parentNode = parentNode.parentNode;
          }
        }

        if (this.isChildOfWysiwygContainer(parentNode)) return parentNode;
      }
    }

    return null;
  };

  /**
   * Set the selected text to the full node
   */
  setSelectedNode = () => {
    const selection = window.getSelection();

    selection.getRangeAt(0).selectNode(this.currentNode);
  };

  /**
   * Check if this element is a child of a wysiwyg-container (or is a wysiwyg-container)
   * @param child
   * @returns {boolean}
   */
  isChildOfWysiwygContainer = (child) => {
    if (child.classList.contains('js-wysiwyg-container')) return true;

    let parent = child.parentNode;

    //Keep checking the parent until we find the class
    while (parent !== null && parent !== 'undefined') {
      //We went too far!
      if (parent.id === "root") break;

      //Is a wysiwyg container
      if (parent.classList.contains('js-wysiwyg-container')) return true;

      //Try the next parent
      parent = parent.parentNode;
    }

    //No more parents to check, the element is not a child of the required class
    return false;
  };

  /**
   * Clear the selection
   */
  clearSelectedText = () => {
    if (window.getSelection) {
      window.getSelection().removeAllRanges();
    }
  };

  /**
   * Hide the form
   */
  hideForm = () => {
    this.shown = false;

    this.node.classList.add('mod-hidden');
    this.overlay.classList.add('mod-hidden');
    this.messageContainer.classList.remove('success');
    this.messageContainer.classList.remove('error');
    this.node.classList.remove('mod-message');
    this.node.classList.remove('mod-loading');
    this.messageContainer.innerText = '';

    this.clearForm();

    this.blurForm();
  };

  /**
   * Show the form
   */
  showForm = () => {
    this.node.classList.remove('mod-hidden');
    this.overlay.classList.remove('mod-hidden');

    this.fillForm();

    this.focusForm();

    window.setTimeout(
      function () {
        this.shown = true;
      }.bind(this),
      10
    )
  };

  /**
   * Handle clicking outside to hide the form
   * @param e
   */
  clickOutsideHandler = (e) => {
    if (this.shown) {

      let isClickInside = this.node.contains(e.target);

      if (!isClickInside) {
        this.hideForm();
      }
    }

    if (this.dialogueShown) {
      let isClickInside = this.dialogue.contains(e.target);

      if (!isClickInside) {
        this.busy = false;

        this.hideDialogue();
      }
    }
  };

  /**
   * Focus on the form by scrolling to it and disabling the scrolling
   */
  focusForm = () => {
    let body = document.querySelector('body');
    body.style.overflow = "hidden";
  };

  /**
   * Handle focusing on a field
   * Makes all errors disappear (because they hover over fields)
   */
  fieldFocusHandler = () => {
    this.toggleError('email', '');
    this.toggleError('newContent', '');
  };

  /**
   * Enable scrolling again
   */
  blurForm = () => {
    let body = document.querySelector('body');
    body.style.overflow = "auto";
  };

  /**
   * Close the form when clicked on the close button
   * @param e
   */
  closeButtonClickHandler = (e) => {
    e.preventDefault();

    this.hideForm();

    this.clearSelectedText();
  };

  /**
   * Check if all fields are filled correctly
   * @returns {number}
   */
  checkFields = () => {
    let errors = 0;

    let emailRegex = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

    //If the email is too short
    if (this.email.value.length < 5) {
      errors++;
      this.toggleError('email', 'Vul a.u.b. uw e-mailadres in.');
    }
    //If the email is not an email
    else if (!emailRegex.test(String(this.email.value).toLowerCase())) {
      errors++;
      this.toggleError('email', 'Vul a.u.b. een geldig e-mailadres in.');
    }
    else {
      this.toggleError('email', '');
    }

    //If the user didn't change the content at all
    if (this.newContent == '' || this.newContent == tinymce.activeEditor.getContent()) {
      errors++;
      this.toggleError('newContent', 'U heeft niets veranderd aan de tekst!');
    }
    else {
      this.toggleError('newContent', '');
    }

    return errors;
  };

  /**
   * Display or hide an error message
   * @param field
   * @param message
   */
  toggleError = (field, message) => {
    let element = this[field + 'Error'];

    if (element) {
      element.innerHTML = message;

      if (message.length > 0) {
        element.classList.remove('mod-hidden');
      }
      else {
        element.classList.add('mod-hidden');
      }
    }
  };

  /**
   * Handle submitting the rectification form
   * @param e
   */
  submitHandler = (e) => {
    e.preventDefault();

    if (this.loading || !this.busy || !this.shown) return;

    //If errors
    if (this.checkFields() > 0) return;

    let oldContent = this.currentNode.outerHTML,
      newContent = tinymce.activeEditor.getContent(),
      userName = this.name.value,
      userEmail = this.email.value,
      thePot = this.thePot.value,
      nid = this.node.dataset.nid;

    // if (newContent.includes('<ul>') && !oldContent.includes('<ul>')) {
    //     newContent = newContent.replace(/<ul>|<\/ul>/gmi, '');
    // }

    if (oldContent.length > 1 && newContent.length > 1 && nid) {
      this.startLoading();

      let xhr = new XMLHttpRequest(),
        body = {
          thePot: thePot,
          newText: newContent,
          oldText: oldContent,
          articleNid: nid,
          userName: userName,
          userEmail: userEmail,
        };

      xhr.open('POST', '/api/rectification');

      xhr.setRequestHeader('Content-Type', 'application/json');

      xhr.onload = function () {
        if (xhr.readyState === 4) {
          if (xhr.status === 200) {
            this.postSuccessHandler(xhr);
          } else {
            console.error(xhr.statusText);
          }
        }
      }.bind(this);

      xhr.send(JSON.stringify(body));
    }
  };

  /**
   * Handle the post success
   * @param xhr
   */
  postSuccessHandler = (xhr) => {
    this.doneLoading();

    let response = JSON.parse(xhr.responseText);

    if (response.success) {
      this.messageContainer.classList.add('success');
    }
    else {
      this.messageContainer.classList.add('error');
    }

    this.node.classList.add('mod-message');
    this.messageContainer.innerHTML = response.message;

    window.setTimeout(
      function () {
        this.hideForm();
      }.bind(this),
      5000
    )
  };

  /**
   * Indicate that the form is loading
   */
  startLoading = () => {
    this.loading = true;

    this.node.classList.add('mod-loading');
  };

  /**
   * Stop indicating that the form is loading
   */
  doneLoading = () => {
    this.loading = false;

    this.node.classList.remove('mod-loading');
  };

  /**
   * Fill the form with the selection HTML
   */
  fillForm = () => {
    if (this.currentNode !== null) {
      tinymce.activeEditor.setContent(this.currentNode.outerHTML);

      //Store the content so we can check if it's different before submitting
      this.newContent = tinymce.activeEditor.getContent();
    }

    //Clear the email & name fields
    this.email.value = '';
    this.name.value = '';
  };

  /**
   * Remove the text from the form
   */
  clearForm = () => {
    tinymce.activeEditor.setContent('');
    this.currentNode = null;
    this.busy = false;
  };
}

export default RectificationForm;



// WEBPACK FOOTER //
// frontend/js/components/article/RectificationForm.js